import type { MediaWarning } from "@anisync/types";
import { BotIcon, LanguagesIcon, TimerIcon } from "lucide-react";
import { formatClock } from "../lib/format";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";

/**
 * Ostrzeżenia o pobranym materiale.
 *
 * Powstały z przeglądu z 12.10.2026: źródła opisują kopie nierzetelnie, a dwóch
 * rzeczy nie umiemy rozstrzygnąć automatycznie — czy napisy są z tłumaczenia
 * maszynowego i czy pod linkiem odcinka nie leży inny odcinek. Widz rozpozna oba
 * w kilka sekund, pod warunkiem że wie, na co patrzeć. Odpowiedzią jest przycisk
 * „Zła wersja" obok, więc tekst mówi, kiedy go użyć.
 *
 * Trzecie — obce napisy — rozstrzygamy, gdy źródło podaje język wprost (Shinden).
 * Ranking spycha takie kopie na koniec, więc ostrzeżenie widać tylko wtedy, gdy
 * innej nie było; mówi to wprost, żeby widz nie szukał lepszej na próżno.
 */

/** Pełna postać — pod obrazem w odtwarzaczu, gdzie jest miejsce na zdanie. */
export function MediaWarningList({ warnings }: { warnings: MediaWarning[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {warnings.map((warning) => (
        <Alert key={warning.kind} variant="warning">
          <WarningIcon warning={warning} />
          <div className="flex flex-col gap-0.5">
            <AlertTitle>{titleOf(warning)}</AlertTitle>
            <AlertDescription>{describe(warning)}</AlertDescription>
          </div>
        </Alert>
      ))}
    </div>
  );
}

/** Krótka postać — w kafelku odcinka, obok rozmiaru i stanu. */
export function MediaWarningBadges({ warnings }: { warnings: MediaWarning[] }) {
  if (warnings.length === 0) return null;
  return (
    <>
      {warnings.map((warning) => (
        <Badge key={warning.kind} variant="warning" title={describe(warning)}>
          <WarningIcon warning={warning} />
          {badgeOf(warning)}
        </Badge>
      ))}
    </>
  );
}

function WarningIcon({ warning }: { warning: MediaWarning }) {
  if (warning.kind === "foreign-subs") return <LanguagesIcon />;
  if (warning.kind === "machine-translation") return <BotIcon />;
  return <TimerIcon />;
}

function badgeOf(warning: MediaWarning): string {
  if (warning.kind === "foreign-subs") return `napisy ${warning.lang.toUpperCase()}`;
  if (warning.kind === "machine-translation") return "napisy AI";
  return "nietypowa długość";
}

function titleOf(warning: MediaWarning): string {
  if (warning.kind === "foreign-subs") return "Napisy w innym języku niż polski";
  return warning.kind === "machine-translation"
    ? "Napisy z tłumaczenia maszynowego"
    : "Nietypowa długość odcinka";
}

/**
 * Treść mówi, CO sprawdzić, a nie tylko, że coś jest nie tak. Ostrzeżenie bez
 * wskazówki zostawia widza z niepokojem i bez decyzji.
 */
function describe(warning: MediaWarning): string {
  if (warning.kind === "foreign-subs") {
    return `Źródło podaje, że napisy tej kopii są w języku ${warning.lang.toUpperCase()}. Pobraliśmy ją, bo polskiej kopii nie było albo żadna nie dała się pobrać. Jeśli polska pojawi się później, „Zła wersja” pobierze ją zamiast tej.`;
  }
  if (warning.kind === "machine-translation") {
    return "Źródło oznaczyło tę kopię jako tłumaczenie AI — napisy mogą być niezręczne albo błędne. Jeśli przeszkadzają, „Zła wersja” pobierze inną kopię, o ile jest.";
  }
  return `Plik trwa ${formatClock(warning.actualSec)}, a odcinek tej serii około ${Math.round(
    warning.expectedSec / 60,
  )} min. To może być podwójny odcinek — albo inny odcinek pod tym linkiem. Sprawdź początek; jeśli to nie ten, użyj „Zła wersja”.`;
}
